import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Briefcase, HeartHandshake, ArrowRight, Sparkles, Shield, Users, ScrollText, Award, Phone, Clock, FileCheck
} from "lucide-react";
import api from "../lib/api";
import { useDocumentSeo, useJsonLd } from "../lib/seo";
import { useScrollReveal, useParallax, useCountUp } from "../lib/motion";

export default function Home() {
    const [info, setInfo] = useState(null);

    useDocumentSeo({
        title: "Admin This! N Thatz Syncd, LLC — SBA-Certified Administrative & Business Services",
        description: "SBA-certified Woman-Owned & Veteran-Owned administrative support, business consulting, business formation, and document management services in Lewisville, TX. Federal contract-ready.",
        canonical: "https://www.ttadmins.com/",
    });
    useJsonLd([
        {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Admin This! N Thatz Syncd, LLC",
            alternateName: "TT Admins",
            url: "https://www.ttadmins.com/",
            potentialAction: {
                "@type": "SearchAction",
                target: "https://www.ttadmins.com/services?q={search_term_string}",
                "query-input": "required name=search_term_string",
            },
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
                {
                    "@type": "Question",
                    name: "What is Admin This! N Thatz Syncd, LLC?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "An SBA-certified Woman-Owned (WOSB) and Veteran-Owned (VOSB) small business providing administrative support, business consulting, LLC formation, document management, and scheduling services from Lewisville, TX.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Are you federal contract-ready?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Yes. We are registered in SAM.gov with UEI MZYVNN8T4B71 and CAGE code 0SQV5, and hold both SBA WOSB and VOSB certifications across 7 NAICS codes covering office administrative services, business consulting, custom programming, and professional training.",
                    },
                },
                {
                    "@type": "Question",
                    name: "Who is the founder?",
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: "Christina Slimp — a U.S. veteran, mother, and servant-leader — is the CEO and founder.",
                    },
                },
            ],
        },
    ]);

    useEffect(() => {
        api.get("/site/info").then(({ data }) => setInfo(data)).catch(() => {});
    }, []);

    useScrollReveal();
    const scrollY = useParallax();

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative dawn-wash overflow-hidden">
                {/* Decorative floating orbs */}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div
                        className="float-orb absolute -top-20 -right-20 w-96 h-96 rounded-full bg-gradient-to-br from-[var(--brand-gold)]/25 to-transparent blur-3xl"
                        style={{ transform: `translate3d(0, ${scrollY * 0.15}px, 0)` }}
                    />
                    <div
                        className="float-orb-delayed absolute -bottom-32 -left-16 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-[var(--brand-coral)]/15 to-transparent blur-3xl"
                        style={{ transform: `translate3d(0, ${scrollY * -0.08}px, 0)` }}
                    />
                    <div className="absolute top-40 left-1/2 w-2 h-2 rounded-full bg-[var(--brand-gold-deep)] opacity-60" />
                    <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 rounded-full bg-[var(--brand-coral)] opacity-70" />
                    {/* Sparkle particles */}
                    <SparkleField />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 fade-up">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-amber-200/70 mb-6 backdrop-blur-sm shadow-sm" data-testid="hero-eyebrow">
                            <span className="w-2 h-2 rounded-full bg-[var(--brand-coral)] animate-pulse" />
                            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-700">SBA-Certified WOSB &amp; VOSB · Federal Contract-Ready</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light leading-[1.02] text-[var(--brand-navy)]" data-testid="hero-h1">
                            Administrative <span className="italic text-[var(--brand-coral)]">excellence</span>
                            <br />
                            for{" "}
                            <span className="word-rotator" aria-label="various business types">
                                <span>growing</span>
                                <span>mission-driven</span>
                                <span>veteran-owned</span>
                                <span>federal-contracting</span>
                            </span>
                            <br />
                            businesses.
                        </h1>
                        <p className="mt-6 text-lg lg:text-xl text-slate-700 max-w-2xl leading-relaxed">
                            Executive support, business consulting, LLC formation, document management, and scheduling — delivered with the structure and stewardship your operation deserves.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/services" className="btn-gold" data-testid="hero-services-btn">
                                Explore Business Services <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                            <Link to="/contact" className="btn-outline-navy" data-testid="hero-consultation-btn">
                                Book a Free Consultation
                            </Link>
                        </div>

                        {/* Animated proof strip */}
                        <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6" data-testid="hero-proof-strip">
                            <StatCard icon={<Award className="w-5 h-5" />} target={2} suffix="×" label="SBA Certifications" sub="WOSB + VOSB" i={1} />
                            <StatCard icon={<Briefcase className="w-5 h-5" />} target={6} label="Core Services" sub="All in one house" i={2} />
                            <StatCard icon={<FileCheck className="w-5 h-5" />} target={7} label="NAICS Codes" sub="Contract-ready" i={3} />
                            <StatCard icon={<Clock className="w-5 h-5" />} target={24} suffix="h" label="Response Time" sub="On every inquiry" i={4} />
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative fade-up delay-2">
                        <div
                            className="relative"
                            style={{ transform: `translate3d(0, ${scrollY * -0.06}px, 0)` }}
                        >
                            <div className="aura-ring" aria-hidden="true" />
                            <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl ring-1 ring-white/60">
                                <img
                                    src="https://images.unsplash.com/photo-1601359865491-c47a8d93159d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                                    alt="Sunrise over clouds"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/50 via-transparent to-transparent" />
                                <div className="absolute bottom-6 left-6 right-6 text-white">
                                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-200/90 mb-2">Our Mission</div>
                                    <p className="font-serif text-2xl leading-snug font-light">
                                        Empowering communities through opportunity and purpose.
                                    </p>
                                    <div className="text-xs mt-2 font-sans text-amber-200/80">— Admin This! N Thatz Syncd, LLC</div>
                                </div>
                            </div>
                        </div>

                        <div className="absolute -top-6 -left-6 hidden md:flex flex-col gap-2 bg-white rounded-3xl shadow-xl border border-slate-100 px-6 py-5 max-w-[240px] z-10">
                            <div className="eyebrow">CEO</div>
                            <div className="font-serif text-lg text-[var(--brand-navy)]">Christina Slimp</div>
                            <div className="text-xs text-slate-600">Veteran. Mother. Servant-leader.</div>
                        </div>
                    </div>
                </div>

                <CertMarquee />
            </section>

            {/* THREE PILLARS */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 reveal">
                <div className="max-w-3xl mb-14">
                    <div className="eyebrow mb-3">Our Three Pillars</div>
                    <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)]">
                        One mission, lived out in two movements.
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
                    <PillarCard
                        to="/services"
                        testid="pillar-services"
                        icon={<Briefcase className="w-7 h-7" />}
                        eyebrow="Pillar 01"
                        title="Business Services"
                        body="Administrative support, consulting, web-automation, and operations sync for organizations called to do great work with excellence and integrity."
                        accent="bg-[var(--brand-sky)]"
                    />
                    <PillarCard
                        to="/workforce"
                        testid="pillar-workforce"
                        icon={<HeartHandshake className="w-7 h-7" />}
                        eyebrow="Pillar 02"
                        title="E.M.P.O.W.E.R.I.N.G Veterans Initiative"
                        body="Housing pathways, online certification training, and daily essentials for ALL homeless veterans and their children. Partners with the VA and HUD."
                        accent="bg-[var(--brand-coral)]"
                    />
                </div>
            </section>

            {/* STORY / WORKFORCE */}
            <section className="sky-wash py-20 lg:py-28 reveal">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        <div className="eyebrow mb-3">Restoration in motion</div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)] mb-6">
                            Every veteran who served deserves a place to call home.
                        </h2>
                        <p className="text-slate-700 leading-relaxed text-lg mb-4">
                            Our <strong>E.M.P.O.W.E.R.I.N.G Veterans Workforce Initiative</strong> walks alongside ALL homeless veterans — with or without children — connecting them to housing, online certification training, professional wardrobes, food, and the daily essentials needed to focus on rebuilding.
                        </p>
                        <p className="text-slate-700 leading-relaxed mb-8">
                            In partnership with the Veteran's Administration and HUD, we strive to end veteran homelessness — one veteran, one family, one second chance at a time.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/workforce" className="btn-sky" data-testid="story-learn-btn">
                                Learn the Mission
                            </Link>
                            <Link to="/workforce#donate" className="btn-outline-navy" data-testid="story-donate-btn">
                                Give a Gift of Hope
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-6 order-1 lg:order-2 relative">
                        <div className="relative rounded-[2rem] overflow-hidden aspect-square shadow-2xl">
                            <img
                                src="https://images.unsplash.com/photo-1748444355580-278e972f660b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                                alt="Female veteran portrait"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-6 -right-6 bg-[var(--brand-navy)] text-white rounded-2xl px-6 py-5 shadow-xl">
                            <div className="text-amber-200 text-3xl font-serif font-light">100%</div>
                            <div className="text-xs uppercase tracking-wider">of every dollar serves veterans</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* PAST PERFORMANCE */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 reveal">
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="eyebrow mb-3">Past Performance</div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)]">
                            Trusted by businesses building with purpose.
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            From startup formation to compliance, we sync the systems that let mission-led organizations operate at full capacity.
                        </p>
                        <Link to="/about" className="btn-outline-navy mt-8" data-testid="past-perf-about-btn">
                            View Capability Statement
                        </Link>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
                        {[
                            { name: "AdminEase Consulting", desc: "Business formation, internal workflow sync, project tracking & document protocols.", icon: <Briefcase /> },
                            { name: "Victoria's KeyGifts", desc: "Business establishment, website creation, community engagement & marketing.", icon: <Sparkles /> },
                            { name: "Hoosier Aire HVAC", desc: "Administrative logistics, data entry & compliance certificates.", icon: <ScrollText /> },
                            { name: "Federal & State", desc: "Grant readiness, OJT programs, partnerships with VA & HUD.", icon: <Users /> },
                        ].map((c, i) => (
                            <div key={c.name} className={`faith-card p-7 fade-up delay-${i + 1}`}>
                                <div className="w-11 h-11 rounded-xl bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)] flex items-center justify-center mb-4">
                                    {c.icon}
                                </div>
                                <div className="font-serif text-xl text-[var(--brand-navy)] mb-2">{c.name}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{c.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="relative night-wash text-white grain overflow-hidden reveal">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
                    <div className="max-w-3xl">
                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-200 mb-4">Begin the conversation</div>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight font-light">
                            Let's sync your mission with operational excellence.
                        </h2>
                        <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                            Whether you need a consultant, a curriculum, or a partner in restoring lives — we're here, ready, and led.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" className="btn-gold" data-testid="final-cta-contact">
                                Book a Consultation <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                            <a href="tel:+19729429022" className="inline-flex items-center text-amber-200 hover:text-amber-100 font-semibold gap-2" data-testid="final-cta-phone">
                                <Phone className="w-4 h-4" /> (972) 942-9022
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function PillarCard({ to, testid, icon, eyebrow, title, body, accent }) {
    return (
        <Link to={to} className="faith-card p-8 block group fade-up" data-testid={testid}>
            <div className={`w-14 h-14 rounded-2xl ${accent} text-white flex items-center justify-center mb-6 group-hover:scale-105 transition-transform`}>
                {icon}
            </div>
            <div className="eyebrow mb-2">{eyebrow}</div>
            <h3 className="font-serif text-2xl text-[var(--brand-navy)] mb-3">{title}</h3>
            <p className="text-slate-600 leading-relaxed text-sm mb-5">{body}</p>
            <div className="inline-flex items-center text-sm font-semibold text-[var(--brand-navy)] group-hover:text-[var(--brand-coral)]">
                Explore <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
            </div>
        </Link>
    );
}

function StatCard({ icon, target, suffix = "", label, sub, i }) {
    const [ref, val] = useCountUp(target, { duration: 1200 + i * 150, suffix });
    return (
        <div
            ref={ref}
            className={`bg-white/60 backdrop-blur-sm border border-white/70 rounded-2xl p-4 fade-up delay-${i} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`}
        >
            <div className="w-8 h-8 rounded-lg bg-[var(--brand-gold)]/20 text-[var(--brand-gold-deep)] flex items-center justify-center mb-2">
                {icon}
            </div>
            <div className="font-serif text-2xl sm:text-3xl text-[var(--brand-navy)] leading-none tabular-nums count-up">{val}</div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-700 mt-1.5">{label}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
        </div>
    );
}

const SPARKS = [
    { top: "18%", left: "8%",  color: "",      delay: "0s"   },
    { top: "62%", left: "14%", color: "coral", delay: "1.2s" },
    { top: "28%", left: "42%", color: "sky",   delay: "2.6s" },
    { top: "78%", left: "38%", color: "",      delay: "3.9s" },
    { top: "12%", left: "72%", color: "coral", delay: "5.1s" },
    { top: "48%", left: "86%", color: "",      delay: "6.4s" },
    { top: "82%", left: "78%", color: "sky",   delay: "7.7s" },
    { top: "36%", left: "22%", color: "",      delay: "4.4s" },
];

function SparkleField() {
    return (
        <>
            {SPARKS.map((s, i) => (
                <span
                    key={i}
                    className={`sparkle ${s.color}`}
                    style={{ top: s.top, left: s.left, animationDelay: s.delay }}
                    aria-hidden="true"
                />
            ))}
        </>
    );
}

function CertMarquee() {
    const items = [
        "SBA Certified WOSB", "SBA Certified VOSB", "UEI: MZYVNN8T4B71", "CAGE Code: 0SQV5",
        "NAICS 561110", "NAICS 541611", "NAICS 541511", "NAICS 611430",
        "Veteran-Owned", "Woman-Owned", "Faith-Based Excellence",
    ];
    return (
        <div className="border-y border-slate-200/70 bg-white/40 backdrop-blur-sm py-5 overflow-hidden" data-testid="cert-marquee">
            <div className="marquee-track">
                {[...items, ...items].map((it, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 whitespace-nowrap">
                        <Award className="w-3.5 h-3.5 text-[var(--brand-coral)]" />
                        {it}
                    </div>
                ))}
            </div>
        </div>
    );
}
